import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Search, ArrowUpDown, Download, Trash2 } from "lucide-react";
import { ClientProfileDialog } from "./clients/profile/ClientProfileDialog";
import { JobDialog } from "./job/JobDialog";
import { CreateOrderDialog } from "./CreateOrderDialog";
import { ClientStatCards } from "./clients/ClientStatCards";
import { ClientFormSheet } from "./clients/ClientFormSheet";
import { ClientTableRow } from "./clients/ClientTableRow";
import { ClientPagination } from "./clients/ClientPagination";
import { ClientMergeDialog } from "./clients/ClientMergeDialog";
import { TYPE_OPTIONS, SORT_OPTIONS, displayName } from "./clients/clientConstants";

const thCls = "text-[11px] font-semibold uppercase tracking-[0.1em] text-[#475467] bg-[#F7F9FC]";

export function ClientsTab({ onChanged, refreshKey, photographers = [] }) {
  const { user } = useAuth();
  const canBulkDelete = user?.role === "admin" && user?.is_primary_owner === true;
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);

  const [resetTarget, setResetTarget] = useState(null);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [archiving, setArchiving] = useState(false);
  const [mergeTarget, setMergeTarget] = useState(null);
  const [formTarget, setFormTarget] = useState(undefined); // undefined=closed, null=add, obj=edit
  const [profileClient, setProfileClient] = useState(null);
  const [orderJob, setOrderJob] = useState(null); // { id, tab }
  const [createOrderFor, setCreateOrderFor] = useState(undefined); // undefined=closed, null=blank, obj=prefilled
  const [selected, setSelected] = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  const loadStats = () => api.get("/admin/clients/stats").then((r) => setStats(r.data)).catch(() => toast.error("Could not load client summary stats"));
  const load = () => api.get("/admin/clients/search", { params: { q, type: typeFilter, status: statusFilter, sort_by: sortBy, page, page_size: pageSize } })
    .then((r) => { setClients(r.data.items); setTotal(r.data.total); }).catch(() => toast.error("Could not load clients. Try refreshing."));

  useEffect(() => { setPage(1); }, [q, typeFilter, statusFilter, sortBy]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, typeFilter, statusFilter, sortBy, page, pageSize, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadStats(); }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setSelected(new Set()); }, [q, typeFilter, statusFilter, sortBy, page, pageSize]);

  const changed = () => { load(); loadStats(); onChanged(); };
  const toggleSort = (a, b) => setSortBy((s) => (s === a ? b : a));
  const toggleSelect = (id, checked) => setSelected((current) => { const next = new Set(current); if (checked) next.add(id); else next.delete(id); return next; });
  const toggleSelectAll = (checked) => setSelected(checked ? new Set(clients.map((c) => c.id)) : new Set());
  const bulkDelete = async () => {
    setBulkDeleting(true);
    try {
      const ids = [...selected];
      const response = await api.post("/admin/clients/bulk-delete", { client_ids: ids });
      const { deleted_ids, undo_token, undo_window_seconds } = response.data;
      setSelected(new Set()); setBulkDeleteOpen(false); changed();
      const label = `${deleted_ids.length} client${deleted_ids.length === 1 ? "" : "s"} deleted`;
      if (undo_token) {
        toast.success(label, {
          duration: (undo_window_seconds || 60) * 1000,
          action: {
            label: <span data-testid="clients-undo-delete-button">Undo</span>,
            onClick: async () => {
              try {
                await api.post("/admin/clients/undo-delete", { undo_token });
                toast.success(`${deleted_ids.length} client${deleted_ids.length === 1 ? "" : "s"} restored`);
                changed();
              } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
            },
          },
        });
      } else {
        toast.success(label);
      }
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setBulkDeleting(false); }
  };

  const exportCsv = async () => {
    try {
      const r = await api.get("/admin/clients/export.csv", { params: { q, type: typeFilter, status: statusFilter }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "clients.csv"; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Could not export clients"); }
  };

  const resetEmail = async () => {
    try { const r = await api.post(`/admin/clients/${resetTarget.id}/reset-password`, { send_email: true }); toast.success(r.data.message); setResetTarget(null); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };
  const archiveOrRestore = async () => {
    if (!archiveTarget || archiving) return;
    setArchiving(true);
    const isArchived = archiveTarget.status === "archived";
    try {
      await api.post(`/admin/clients/${archiveTarget.id}/${isArchived ? "restore" : "archive"}`);
      toast.success(isArchived ? `${displayName(archiveTarget)} was restored.` : `${displayName(archiveTarget)} was archived. Orders and history are kept.`);
      setArchiveTarget(null); changed();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); } finally { setArchiving(false); }
  };

  return (
    <div className="space-y-5 fade-up" data-testid="clients-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="eyebrow">Accounts</span>
          <h1 className="font-serif-display text-4xl sm:text-[42px] mt-2 text-[#101828]">Clients</h1>
          <p className="text-sm text-[#475467] mt-3">Manage client profiles, bookings, invoices, and account information.</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="export-clients-button" variant="outline" onClick={exportCsv} className="rounded-xl h-12 px-5 border-[#E4E9F0] text-[#475467] gap-2">
            <Download className="h-4 w-4" strokeWidth={1.75} /> Export
          </Button>
          <Button data-testid="add-client-button" onClick={() => setFormTarget(null)} className="rounded-xl h-12 px-[22px] bg-[#0C2347] hover:bg-[#123262] text-white gap-2 shadow-[0_3px_8px_rgba(12,35,71,0.13)] transition-all hover:-translate-y-px">
            <Plus className="h-4 w-4" strokeWidth={2} /> Add New Client
          </Button>
        </div>
      </header>

      <ClientStatCards stats={stats} />

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[260px] max-w-xl">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" strokeWidth={1.5} />
          <Input data-testid="clients-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search clients by name, company, email, or phone..." className="rounded-xl border-[#E4E9F0] pl-9 h-12" />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger data-testid="clients-type-filter" className="w-[170px] rounded-xl h-12"><SelectValue placeholder="All Client Types" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Client Types</SelectItem>{TYPE_OPTIONS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="clients-status-filter" className="w-[150px] rounded-xl h-12"><SelectValue placeholder="All Statuses" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Statuses</SelectItem><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem><SelectItem value="archived">Archived</SelectItem></SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger data-testid="clients-sort-dropdown" className="w-[200px] rounded-xl h-12 ml-auto"><SelectValue /></SelectTrigger>
          <SelectContent>{SORT_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {canBulkDelete && selected.size > 0 && (
        <div data-testid="clients-bulk-bar" className="flex items-center gap-3 rounded-xl border border-[#E4E9F0] bg-[#F7F9FC] px-4 py-2.5 text-xs font-mono text-[#475467]">
          <span data-testid="clients-bulk-count">{selected.size} selected</span>
          <button data-testid="clients-bulk-select-all" type="button" onClick={() => toggleSelectAll(true)} className="rounded-lg border border-[#E4E9F0] bg-white px-2.5 py-1 hover:border-[#0C2347]/30 transition-colors">Select all on page</button>
          <button data-testid="clients-bulk-clear" type="button" onClick={() => toggleSelectAll(false)} className="rounded-lg border border-[#E4E9F0] bg-white px-2.5 py-1 hover:border-[#0C2347]/30 transition-colors">Clear</button>
          <button data-testid="clients-bulk-delete" type="button" onClick={() => setBulkDeleteOpen(true)} className="rounded-lg border border-[#F3D9D7] bg-white px-2.5 py-1 text-[#B3261E] hover:bg-[#FBEAE9] transition-colors flex items-center gap-1.5"><Trash2 size={12} /> Delete selected</button>
        </div>
      )}

      <div className="rounded-[14px] border border-[#E4E9F0] bg-white overflow-hidden" style={{ boxShadow: "0 4px 20px rgba(15,23,42,0.035)" }}>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow className="hover:bg-transparent">
              {canBulkDelete && <TableHead className={`${thCls} w-10`}><input type="checkbox" data-testid="clients-select-all" aria-label="Select all clients on this page" checked={clients.length > 0 && selected.size === clients.length} onChange={(e) => toggleSelectAll(e.target.checked)} className="h-4 w-4 rounded border-[#CBD5E1] accent-[#0C2347]" /></TableHead>}
              <TableHead className={thCls}><button data-testid="clients-sort-name" onClick={() => toggleSort("name_asc", "name_desc")} className="inline-flex items-center gap-1 uppercase">Client <ArrowUpDown size={12} /></button></TableHead>
              <TableHead className={thCls}>Company / Brokerage</TableHead>
              <TableHead className={thCls}>Contact</TableHead>
              <TableHead className={thCls}>Type</TableHead>
              <TableHead className={thCls}><button data-testid="clients-sort-orders" onClick={() => toggleSort("most_orders", "fewest_orders")} className="inline-flex items-center gap-1 uppercase">Orders <ArrowUpDown size={12} /></button></TableHead>
              <TableHead className={thCls}><button data-testid="clients-sort-last-order" onClick={() => toggleSort("recent_order", "oldest_order")} className="inline-flex items-center gap-1 uppercase">Last Order <ArrowUpDown size={12} /></button></TableHead>
              <TableHead className={thCls}>Status</TableHead>
              <TableHead className={thCls}></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {clients.map((c) => (
                <ClientTableRow key={c.id} client={c}
                  selected={selected.has(c.id)}
                  onToggleSelect={canBulkDelete ? toggleSelect : undefined}
                  onView={() => setProfileClient(c)}
                  onEdit={() => setFormTarget(c)}
                  onReset={() => setResetTarget(c)}
                  onArchive={() => setArchiveTarget(c)}
                  onRestore={() => setArchiveTarget(c)}
                  onMerge={() => setMergeTarget(c)} />
              ))}
              {clients.length === 0 && <TableRow><TableCell colSpan={canBulkDelete ? 9 : 8} className="text-center text-slate-400 py-14 text-sm">No clients found.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
        <div className="border-t border-[#E4E9F0]"><ClientPagination page={page} pageSize={pageSize} total={total} onPageChange={setPage} onPageSizeChange={(n) => { setPageSize(n); setPage(1); }} /></div>
      </div>

      {formTarget !== undefined && <ClientFormSheet client={formTarget} onClose={() => setFormTarget(undefined)} onSaved={(client) => { setFormTarget(undefined); changed(); if (!formTarget) setProfileClient(client); else if (profileClient?.id === client.id) setProfileClient(client); }} />}
      {profileClient && <ClientProfileDialog client={profileClient} onClose={() => setProfileClient(null)} onEdit={(c) => setFormTarget(c)} onOpenOrder={(id, tab) => setOrderJob({ id, tab })} onCreateOrder={(c) => setCreateOrderFor(c)} refreshKey={refreshKey} />}
      {orderJob && <JobDialog bookingId={orderJob.id} initialTab={orderJob.tab} photographers={photographers} onClose={() => setOrderJob(null)} onChanged={changed} />}
      {createOrderFor !== undefined && <CreateOrderDialog initialClient={createOrderFor} photographers={photographers} onClose={() => setCreateOrderFor(undefined)} onCreated={() => { setCreateOrderFor(undefined); changed(); }} />}

      {resetTarget && (
        <Dialog open onOpenChange={() => setResetTarget(null)}>
          <DialogContent className="rounded-xl" data-testid="reset-dialog">
            <DialogHeader><DialogTitle className="font-serif-display text-3xl font-normal">Reset password</DialogTitle><DialogDescription className="sr-only">Send a secure, expiring password reset link to this client.</DialogDescription></DialogHeader>
            <p className="text-sm text-slate-500">A secure password-reset link, valid for one hour, will be emailed to {displayName(resetTarget)} ({resetTarget.email}). Administrators never see client passwords.</p>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button data-testid="reset-email-btn" className="rounded-xl bg-[#0C2347] hover:bg-[#123262] text-white" onClick={resetEmail}>Send secure reset email</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {archiveTarget && (() => {
        const isArchived = archiveTarget.status === "archived";
        let confirmLabel = "Yes, archive";
        if (archiving) confirmLabel = "Please wait…";
        else if (isArchived) confirmLabel = "Yes, restore";
        return (
        <Dialog open onOpenChange={(open) => !open && !archiving && setArchiveTarget(null)}>
          <DialogContent className="rounded-xl" data-testid="archive-client-confirmation">
            <DialogHeader><DialogTitle className="font-serif-display text-3xl font-normal">{isArchived ? "Restore this client?" : "Archive this client?"}</DialogTitle>
              <DialogDescription>{isArchived ? `${displayName(archiveTarget)} will become active again and reappear in your main client list.` : `${displayName(archiveTarget)} will be hidden from your active client list. All orders, invoices, and galleries stay intact and this can be undone anytime.`}</DialogDescription></DialogHeader>
            <DialogFooter className="gap-2">
              <Button data-testid="archive-client-no" variant="ghost" disabled={archiving} onClick={() => setArchiveTarget(null)}>Cancel</Button>
              <Button data-testid="archive-client-yes" disabled={archiving} onClick={archiveOrRestore} className="rounded-xl bg-[#0C2347] hover:bg-[#123262] text-white">{confirmLabel}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        );
      })()}

      {mergeTarget && <ClientMergeDialog client={mergeTarget} onClose={() => setMergeTarget(null)} onMerged={() => { setMergeTarget(null); changed(); }} />}

      {bulkDeleteOpen && (
        <Dialog open onOpenChange={(open) => !open && !bulkDeleting && setBulkDeleteOpen(false)}>
          <DialogContent className="rounded-xl" data-testid="bulk-delete-clients-confirmation">
            <DialogHeader><DialogTitle className="font-serif-display text-3xl font-normal">Delete {selected.size} client{selected.size === 1 ? "" : "s"}?</DialogTitle>
              <DialogDescription>This permanently deletes the selected client profile{selected.size === 1 ? "" : "s"} and their notes. Their past orders are kept for your records but will no longer be linked to a client account. This cannot be undone.</DialogDescription></DialogHeader>
            <DialogFooter className="gap-2">
              <Button data-testid="bulk-delete-clients-cancel" variant="ghost" disabled={bulkDeleting} onClick={() => setBulkDeleteOpen(false)}>Cancel</Button>
              <Button data-testid="bulk-delete-clients-confirm" disabled={bulkDeleting} onClick={bulkDelete} className="rounded-xl bg-red-700 hover:bg-red-800 text-white">{bulkDeleting ? "Deleting…" : "Yes, delete permanently"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
